import { DbBook, DbChapter } from '@voyager/types';
import { Book } from '../types/Book';
import { consumerApiClient } from './apiClient';
import { 
  transformDbBookToBook, 
  transformDbBooksToBooks,
  extractChapterContent,
  getChapterTitle as getChapterTitleFromDb,
  getChapterChoiceText as getChapterChoiceTextFromDb
} from './dataTransforms';

/**
 * API-based book service that fetches data from Creator app D1 database
 * Replaces the file-based bookService.ts
 */
class ApiBookService {
  
  /**
   * Load all published books from API
   */
  async loadBooks(): Promise<Book[]> {
    try {
      console.log('📚 Loading books from API...');
      
      const response = await consumerApiClient.request<DbBook[]>('/public/books');
      
      if (!response.success) {
        console.error('❌ Failed to load books:', response.error?.message);
        throw new Error(response.error?.message || 'Failed to load books');
      }

      const dbBooks = response.data || [];
      const books = transformDbBooksToBooks(dbBooks);
      
      console.log(`✅ Loaded ${books.length} books from API`);
      return books;
      
    } catch (error) {
      console.error('❌ Error loading books from API:', error);
      throw error;
    }
  }

  /**
   * Load single book by ID from API
   */
  async loadBook(bookId: string): Promise<Book | null> {
    try {
      console.log(`📖 Loading book ${bookId} from API...`);
      
      // Get book data
      const bookResponse = await consumerApiClient.request<DbBook>(`/public/books/${bookId}`);
      
      if (!bookResponse.success) {
        if (bookResponse.error?.code === 'NOT_FOUND_ERROR') {
          console.warn(`📖 Book ${bookId} not found`);
          return null;
        }
        throw new Error(bookResponse.error?.message || 'Failed to load book');
      }

      const dbBook = bookResponse.data;
      if (!dbBook) {
        console.warn(`📖 Book ${bookId} not found`);
        return null;
      }

      // Get chapters for this book
      const chaptersResponse = await consumerApiClient.request<DbChapter[]>(`/public/books/${bookId}/chapters`);
      
      if (!chaptersResponse.success) {
        console.warn(`📖 Failed to load chapters for book ${bookId}:`, chaptersResponse.error?.message);
        // Continue without chapters rather than failing completely
      }

      const dbChapters = chaptersResponse.data || [];
      const book = transformDbBookToBook(dbBook, dbChapters);
      
      console.log(`✅ Loaded book: ${book.title} with ${book.chapters.length} chapters`);
      return book;
      
    } catch (error) {
      console.error(`❌ Error loading book ${bookId}:`, error);
      return null;
    }
  }

  /**
   * Load chapter content - now from database content field
   */
  async loadChapterContent(bookId: string, chapterFile: string): Promise<string | null> {
    try {
      // Extract chapter ID from file name (remove .md extension)
      const chapterId = chapterFile.replace('.md', '');
      
      console.log(`📄 Loading chapter content for ${bookId}/${chapterId}...`);
      
      // Get chapters for this book
      const chaptersResponse = await consumerApiClient.request<DbChapter[]>(`/public/books/${bookId}/chapters`);
      
      if (!chaptersResponse.success) {
        console.error(`❌ Failed to load chapters for ${bookId}:`, chaptersResponse.error?.message);
        return null;
      }

      const chapters = chaptersResponse.data || [];
      const chapter = chapters.find(ch => ch.id === chapterId);
      
      if (!chapter) {
        console.warn(`📄 Chapter ${chapterId} not found in book ${bookId}`);
        return null;
      }

      const content = extractChapterContent(chapter);
      console.log(`✅ Loaded chapter content: ${content.length} characters`);
      return content;
      
    } catch (error) {
      console.error(`❌ Error loading chapter content ${bookId}/${chapterFile}:`, error);
      return null;
    }
  }

  /**
   * Get chapter choice text from database
   */
  async getChapterChoiceText(bookId: string, chapterFile: string): Promise<string> {
    try {
      const chapterId = chapterFile.replace('.md', '');
      
      // Get chapters for this book
      const chaptersResponse = await consumerApiClient.request<DbChapter[]>(`/public/books/${bookId}/chapters`);
      
      if (!chaptersResponse.success) {
        return `Chapter ${chapterFile.replace('.md', '')}`;
      }

      const chapters = chaptersResponse.data || [];
      const chapter = chapters.find(ch => ch.id === chapterId);
      
      if (!chapter) {
        return `Chapter ${chapterFile.replace('.md', '')}`;
      }

      return getChapterChoiceTextFromDb(chapter);
      
    } catch (error) {
      console.warn(`⚠️ Failed to get chapter choice text for ${bookId}/${chapterFile}:`, error);
      return `Chapter ${chapterFile.replace('.md', '')}`;
    }
  }

  /**
   * Get chapter title from database
   */
  async getChapterTitle(bookId: string, chapterFile: string): Promise<string> {
    try {
      const chapterId = chapterFile.replace('.md', '');
      
      // Get chapters for this book
      const chaptersResponse = await consumerApiClient.request<DbChapter[]>(`/public/books/${bookId}/chapters`);
      
      if (!chaptersResponse.success) {
        return `Chapter ${chapterFile.replace('.md', '')}`;
      }

      const chapters = chaptersResponse.data || [];
      const chapter = chapters.find(ch => ch.id === chapterId);
      
      if (!chapter) {
        return `Chapter ${chapterFile.replace('.md', '')}`;
      }

      return getChapterTitleFromDb(chapter);
      
    } catch (error) {
      console.warn(`⚠️ Failed to get chapter title for ${bookId}/${chapterFile}:`, error);
      return `Chapter ${chapterFile.replace('.md', '')}`;
    }
  }

  /**
   * Health check - test if API is available
   */
  async isApiAvailable(): Promise<boolean> {
    return await consumerApiClient.healthCheck();
  }
}

// Export singleton instance
export const apiBookService = new ApiBookService();

// Export functions for backward compatibility with existing code
export async function loadBooks(): Promise<Book[]> {
  return apiBookService.loadBooks();
}

export async function loadBook(bookId: string): Promise<Book | null> {
  return apiBookService.loadBook(bookId);
}

export async function loadChapterContent(bookId: string, chapterFile: string): Promise<string | null> {
  return apiBookService.loadChapterContent(bookId, chapterFile);
}

export async function getChapterChoiceText(bookId: string, chapterFile: string): Promise<string> {
  return apiBookService.getChapterChoiceText(bookId, chapterFile);
}

export async function getChapterTitle(bookId: string, chapterFile: string): Promise<string> {
  return apiBookService.getChapterTitle(bookId, chapterFile);
}